import React, { useState, useRef } from "react";
import Modal from "components/modals/appointmentModal";
import RescheduleModal from "components/modals/reschedule";
import ChatModal from "components/modals/chatModal";
import FollowUpModal from "components/modals/followUp";
import AudioIcon from "components/AudioIcon";
import {
  displayDateFormatter,
  DataGridCustomPagination,
  open_billing_charge_entry,
  isoDateFormatter,
  addMinutes,
} from "components/helpers";
import { startOfWeek, addDays, format } from "date-fns";
import CalendarHeader from "components/CalendarHeader";
import RemindersModal from "components/modals/RemindersModal";
import AddPatient from "components/modals/AddPatient";
import useSnackbar from "components/customHooks/useSnackbar";
import VisibilityIcon from "@material-ui/icons/Visibility";
import PrintIcon from "@material-ui/icons/Print";
import MicIcon from "@material-ui/icons/Mic";

import { useUserData } from "components/customHooks/userDataContext";
import {
  printEncounterForms,
  updateAppointmentProvider,
} from "apiHandler/actions";

import { confirm_status_array } from "utils/constants";
import PrintEncounterForm from "components/PrintEncounterForm";
import UploadDictationModal from "components/modals/UploadDictation";
import { getAppointments } from "apiHandler/actions";
import { useLocation, useNavigate } from "react-router-dom";
import { DataGrid } from "@mui/x-data-grid";
import {
  Button,
  CircularProgress,
  IconButton,
  MenuItem,
  Select,
  Tooltip,
} from "@mui/material";
import { withStyles } from "@mui/styles";
import { trimmedDate } from "utils/helper";
import { NoteAdd } from "@mui/icons-material";
import PreCharting from "components/modals/preCharting";
import AdvChartSelector from "components/UI/advChartSelector";

const StyledDataGrid = withStyles({
  root: {
    "& .MuiDataGrid-cell": {
      padding: "0px",
      boxSizing: "border-box",
    },
    "& .MuiDataGrid-renderingZone": {
      transform: "none !important",
    },
    "& .MuiDataGrid-viewport": {
      position: "static",
      overflow: "visible",
    },
  },
})(DataGrid);

const getStatus = (confirm_status) => {
  if (
    confirm_status === "Not confirmed" ||
    confirm_status === "Not Confirmed"
  ) {
    return "Not Confirmed";
  }
  if (confirm_status === "Confirmed" || confirm_status === "confirmed") {
    return "Confirmed";
  }
  return "";
};

export const StatusSelector = ({ data, token }) => {
  const [status, setStatus] = useState(getStatus(data.confirm_status));
  const current_status = useRef(getStatus(data.confirm_status));

  const onChange = async (newStatus) => {
    setStatus(newStatus);

    const payload = {
      id: data.id,
      confirm_status: newStatus,
    };

    try {
      const response = await updateAppointmentProvider(payload, token);
      if (response) {
        current_status.current = newStatus;
      } else {
        setStatus(current_status.current);
      }
    } catch (error) {
      console.log("error: ", error);

      setStatus(current_status.current);
    }
  };

  return (
    <Select
      sx={{ width: "100%", height: "100%" }}
      value={status}
      onChange={(e) => onChange(e.target.value)}
      variant="standard"
    >
      {confirm_status_array.map((s) => (
        <MenuItem key={s} value={s} className="text-capitalize">
          {s}
        </MenuItem>
      ))}
    </Select>
  );
};

const Appointments = () => {
  const [filter, setFilter] = useState("viewall");
  const [currentViewName, setCurrentViewName] = useState("Day");
  const [showModal, setShowModal] = useState(false);
  const [selAppt, setSelAppt] = useState(false);
  const [date, setDate] = useState(new Date());
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const [showFollowUpModal, setShowFollowUpModal] = useState(false);
  const [showChatModal, setShowChatModal] = useState(false);
  const [showRemindersModal, setShowRemindersModal] = useState(false);
  const [uploadDictn, setUploadDict] = useState(false);
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [loadingPrint, setLoadingPrint] = useState(null);
  const [printPage, setPrintPage] = useState(null);
  const [currentPlaying, setCurrentPlaying] = useState(null);
  const [openPreChart, setOpenPreChart] = useState(false);

  const [row, setRow] = useState(null);

  const navigate = useNavigate();
  const userData = useUserData();
  const userType = userData?.userType;
  const isProvider = userData?.user_details?.user_role === "Provider";

  const token = userData?.access_token;
  const { CustomSnackbar, setSnackbarMessage } = useSnackbar();
  const location = useLocation();

  const normalizeAppointments = (data = []) => {
    const map = new Map();

    data.forEach((item) => {
      const id = String(item.id);

      const mappedItem = {
        ...item,
        patient_name: `${item.first_name || ""} ${item.last_name || ""}`.trim(),
        title: `${item.first_name || ""} ${item.last_name || ""}`.trim(),
        startDate: `${isoDateFormatter(item.dos)}T${
          item.dos_time && addMinutes(item.dos_time, 0)
        }`,
        endDate: `${isoDateFormatter(item.dos)}T${
          item.dos_time && addMinutes(item.dos_time, parseFloat(item.duration))
        }`,
        appntId: item.id,
      };

      const existing = map.get(id);

      if (!existing) {
        map.set(id, mappedItem);
      } else {
        map.set(id, {
          ...existing,
          ...mappedItem,
          precharting_id: mappedItem.precharting_id || existing.precharting_id,
        });
      }
    });

    return Array.from(map.values()).sort((item1, item2) => {
      if (item1.startDate < item2.startDate) return -1;
      if (item1.startDate > item2.startDate) return 1;
      return 0;
    });
  };

  const setUniqueAppointments = (data = []) => {
    setAppointments(normalizeAppointments(data));
  };

  const fetchData = async (useLocationState = true) => {
    setAppointments([]);
    setLoading(true);
    const payload = {
      search_criteria: {
        practice_id: `${userData.selected_practice}`,
        user_id: userData?.email,
        ...(isProvider && { npi: userData?.user_details?.npi }),
      },
    };
    let startDateTime;
    let endDateTime;
    if (currentViewName === "Month") {
      startDateTime = `${date.getMonth() + 1}/01/${date.getFullYear()}`;
      endDateTime = `${date.getMonth() + 2}/01/${date.getFullYear()}`;
    } else if (currentViewName === "Week") {
      startDateTime = startOfWeek(date);
      endDateTime = addDays(startDateTime, 7);
      startDateTime = displayDateFormatter(startDateTime);
      endDateTime = displayDateFormatter(endDateTime);
    }
    if (location?.state && useLocationState) {
      const { dos, location: appt_location, reminder_status } = location.state;
      if (dos) payload["search_criteria"]["dos"] = displayDateFormatter(dos);
      if (appt_location) payload["search_criteria"]["location"] = appt_location;
      if (reminder_status)
        payload["search_criteria"]["reminder_status"] = reminder_status;
    } else {
      if (startDateTime && endDateTime) {
        payload["search_criteria"]["start_dos"] = startDateTime;
        payload["search_criteria"]["end_dos"] = endDateTime;
      } else {
        payload["search_criteria"]["dos"] = format(date, "MM/dd/yyyy");
      }
    }

    const response = await getAppointments(payload, token);

    if (response) {
      setUniqueAppointments(response);
    } else {
      setAppointments([]);
      setSnackbarMessage("Failed To Fetch Appointments");
    }

    setLoading(false);
  };

  const onPrint = async (appt) => {
    await setPrintPage([]);
    if (loadingPrint) return;
    let payload = {
      practice_id: `${userData.selected_practice}`,
      dos: displayDateFormatter(date),
    };
    if (appt) {
      setLoadingPrint(appt.appntId);
      payload = {
        practice_id: `${userData.selected_practice}`,
        dos: displayDateFormatter(appt?.dos),
        appntId: appt?.appntId,
      };
    }
    const response = await printEncounterForms(payload, token);
    if (response) {
      const {
        insurance_balance_data,
        encounter_list_data,
        dia_third_length,
        diagnosis_codes,
        office_visits,
        patient_balance_data,
        patient_balance_data_length,
        proc_third_length,
        procedure_codes,
      } = response;
      await setPrintPage(
        encounter_list_data.map((enc) => ({
          ...enc,
          dia_third_length,
          diagnosis_codes,
          office_visits,
          patient_balance_data,
          patient_balance_data_length,
          proc_third_length,
          procedure_codes,
          payer_name_primary:
            insurance_balance_data[enc.patient_id]?.["payer_name_primary"],
          payer_name_secondary:
            insurance_balance_data[enc.patient_id]?.["payer_name_secondary"],
          payer_name_tertiary:
            insurance_balance_data[enc.patient_id]?.["payer_name_tertiary"],
          total_balance:
            insurance_balance_data[enc.patient_id]?.["total_balance"],
          patient_portion_balance:
            insurance_balance_data[enc.patient_id]?.["patient_portion_balance"],
          insurance_portion_balance:
            insurance_balance_data[enc.patient_id]?.[
              "insurance_portion_balance"
            ],
        })),
      );
      await window.print();
      await setPrintPage(null);
    } else {
      setSnackbarMessage("Failed to get Encounter Form");
    }
    await setLoadingPrint(null);
  };

  const onCLosePreChart = () => {
    setOpenPreChart(false);
  };

  const onOpenPreChart = (row) => {
    setOpenPreChart(true);
    setRow(row);
  };

  const columns = [
    {
      field: "patient_id",
      headerName: "Connect",
      align: "left",
      width: 220,
      renderCell: (e) => (
        <div>
          <button
            onClick={() => {
              setSelAppt(e.row);
              // if (e.row.appntStatus === "noshow") setShowRescheduleModal(true);
              // else if (e.row.appntStatus === "completed")
              // 	setShowFollowUpModal(true);
              // else setShowModal(true);
              setShowRemindersModal(true);
            }}
            style={{ fontSize: 16 }}
            className="redButton"
          >
            View
          </button>
          <button
            className="redButton"
            style={{ fontSize: 16, marginLeft: 16 }}
            onClick={() => {
              setSelAppt(e.row);
              setShowModal(true);
            }}
          >
            Edit
          </button>

          {e.row.charge_entry_id ? (
            <IconButton
              className="redButton"
              onClick={() => {
                open_billing_charge_entry({
                  patient_id: e.row.patient_id,
                  charge_entry_id: e.row.charge_entry_id,
                });
              }}
            >
              <VisibilityIcon color="secondary" />
            </IconButton>
          ) : null}
          <IconButton onClick={() => onPrint(e.row)}>
            {loadingPrint === e.row.appntId ? (
              <CircularProgress color="secondary" size={20} />
            ) : (
              <PrintIcon color="secondary" />
            )}
          </IconButton>
          {userType === "scribe" && e.row.dictation_id === null ? (
            <IconButton
              onClick={() => {
                setUploadDict(true);
                setSelAppt(e.row);
              }}
            >
              <MicIcon color="secondary" />
            </IconButton>
          ) : null}
          {/* <Link
						to={{
							pathname: "/connect",
							data: e.row,
						}}
						className="appnt-connect-btn"
					>
						Connect
					</Link> */}
          {e.row.precharting_id && (
            <Tooltip title="Pre-Charting">
              <IconButton
                onClick={() =>
                  navigate(`/encounter-new/${e.row.id}`, {
                    state: { data: e.row },
                  })
                }
              >
                <NoteAdd color="secondary" />
              </IconButton>
            </Tooltip>
          )}
        </div>
      ),
    },
    {
      field: "audio_duration",
      headerName: "Recording",
      width: 140,
      align: "center",
      filterable: false,
      renderCell: (e) => {
        return e.row.dictation_id ? (
          <div className="list-duration ml-10">
            <AudioIcon
              showPlayer={true}
              id={e.row.dictation_id}
              currentPlaying={currentPlaying}
              setCurrentPlaying={setCurrentPlaying}
              practice_id={e.row.practice_id}
              npi={e.row.npi}
              ext={e.row.file_extension}
            />
          </div>
        ) : (
          "--"
        );
      },
    },
    {
      field: "id",
      headerName: "ID",
      width: 100,
      align: "center",
    },
    {
      field: "provider_name",
      headerName: "Provider Name",
      width: 150,
    },
    {
      field: "patient_name",
      headerName: "Patient Name",
      minWidth: 200,
      flex: 1,
      renderCell: (e) => {
        return (
          <Button
            variant="text"
            onClick={() =>
              navigate(
                `/patients-details/${e.row.first_name}-${e.row.last_name}`,
                { state: e.row },
              )
            }
            className="ml-10"
          >
            {e.row.first_name} {e.row.last_name}
          </Button>
        );
      },

      // ),
    },
    {
      field: "location",
      headerName: "Location",
      width: 250,
    },

    {
      field: "dob",
      headerName: "DOB",
      width: 150,
      renderCell: (e) => <div>{trimmedDate(e.value)}</div>,
    },
    {
      field: "dos",
      headerName: "Date of service",
      width: 150,
      align: "center",
      renderCell: (e) => <div>{trimmedDate(e.value)}</div>,
    },
    {
      field: "startDate",
      headerName: "Time",
      width: 100,
      renderCell: (e) => <div>{e.row.dos_time}</div>,
    },
    {
      field: "appt_type",
      headerName: "Appnt Type",
      width: 250,
    },
    {
      field: "chief_complaint",
      headerName: "Chief Complaint",
      align: "center",
      minWidth: 120,
      flex: 1,
    },
    {
      field: "adv_chart_status",
      headerName: "Adv Chart Status",
      width: 170,
      renderCell: (e) => <AdvChartSelector data={e.row} token={token} />,
    },
    {
      field: "confirm_status",
      headerName: "Status",
      width: 170,
      renderCell: (e) => <StatusSelector data={e.row} token={token} />,
    },
    {
      field: "reminder_status",
      headerName: "Reminder Status",
      width: 150,
    },
  ];

  return (
    <>
      <div className="list-datagrid">
        <CustomSnackbar />
        <CalendarHeader
          filter={filter}
          setFilter={setFilter}
          currentViewName={currentViewName}
          setCurrentViewName={setCurrentViewName}
          appointments={appointments}
          setAppointments={setUniqueAppointments}
          loading={loading}
          setLoading={setLoading}
          date={date}
          data={appointments}
          setDate={setDate}
          isAppointment={true}
          onPrint={onPrint}
        />
        <StyledDataGrid
          rows={loading ? [] : appointments}
          columns={columns}
          autoHeight
          disableRowSelectionOnClick
          pagination
          pageSize={50}
          getRowId={(row) => String(row.id)}
          density="compact"
          slots={{
            pagination: DataGridCustomPagination,
          }}
          loading={loading}
        />
        {showModal && selAppt && (
          <Modal
            {...selAppt}
            appointment={selAppt}
            onClose={() => {
              setShowModal(false);
              setSelAppt(false);
            }}
            showRescheduleModal={() => {
              setShowModal(false);
              setShowRescheduleModal(true);
            }}
            showChatModal={() => {
              setShowModal(false);
              setShowChatModal(true);
            }}
          />
        )}
        {showRescheduleModal && (
          <RescheduleModal
            {...selAppt}
            onClose={() => {
              setShowRescheduleModal(false);
              setSelAppt(false);
            }}
          />
        )}
        {showFollowUpModal && (
          <FollowUpModal
            {...selAppt}
            onClose={() => {
              setShowFollowUpModal(false);
              setSelAppt(false);
            }}
          />
        )}
        {showChatModal && (
          <ChatModal
            {...selAppt}
            onClose={() => {
              setShowChatModal(false);
              setSelAppt(false);
            }}
          />
        )}
        {showRemindersModal && (
          <RemindersModal
            onClose={() => setShowRemindersModal(false)}
            appointment={selAppt}
          />
        )}
        {selectedPatient === null ? null : (
          <AddPatient
            onClose={() => setSelectedPatient(null)}
            patient_details={selectedPatient}
            setSnackbarMessage={setSnackbarMessage}
          />
        )}
        {uploadDictn && (
          <UploadDictationModal
            onClose={() => setUploadDict(false)}
            setSnackbarMessage={setSnackbarMessage}
            selectedAppt={selAppt}
            setAppointments={setUniqueAppointments}
            fetchData={fetchData}
          />
        )}
      </div>
      <PreCharting open={openPreChart} onClose={onCLosePreChart} row={row} />
      {printPage === null ? null : (
        <>
          {printPage.map((params, i) => {
            return <PrintEncounterForm key={i} params={params} />;
          })}
        </>
      )}
    </>
  );
};

export default Appointments;
